import React, { useState, useEffect, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, Input, Button } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import config from '../../config';
import NewBooking from '../customers/NewBooking';
import { useChat } from '../../contexts/ChatContext';
import styles from './ConversationDetailsModal.module.scss';
import './ConversationDetailsModal.css';

// ============================================================================
// WHATSAPP MARKDOWN PARSER
// ============================================================================
/**
 * Converts WhatsApp-style markdown to React elements:
 *   *bold*  →  <strong>
 *   _italic_ →  <em>
 *   ~strike~ →  <s>
 *   `mono`   →  <code>
 *   newlines →  <br />
 */
const parseWhatsAppMarkdown = (text) => {
  if (!text || typeof text !== 'string') return text;

  const tokenize = (str) => {
    const pattern = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|`[^`\n]+`)/g;
    const tokens = [];
    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(str)) !== null) {
      if (match.index > lastIndex) {
        tokens.push({ type: 'text', value: str.slice(lastIndex, match.index) });
      }
      const raw = match[0];
      if (raw.startsWith('*'))       tokens.push({ type: 'bold',   value: raw.slice(1, -1) });
      else if (raw.startsWith('_')) tokens.push({ type: 'italic', value: raw.slice(1, -1) });
      else if (raw.startsWith('~')) tokens.push({ type: 'strike', value: raw.slice(1, -1) });
      else if (raw.startsWith('`')) tokens.push({ type: 'code',   value: raw.slice(1, -1) });
      lastIndex = match.index + raw.length;
    }

    if (lastIndex < str.length) {
      tokens.push({ type: 'text', value: str.slice(lastIndex) });
    }
    return tokens;
  };

  const renderToken = (token, idx) => {
    switch (token.type) {
      case 'bold':   return <strong key={idx}>{token.value}</strong>;
      case 'italic': return <em key={idx}>{token.value}</em>;
      case 'strike': return <s key={idx}>{token.value}</s>;
      case 'code':   return <code key={idx} style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,0.08)', borderRadius: '3px', padding: '0 3px' }}>{token.value}</code>;
      default:       return token.value;
    }
  };

  const lines = text.split('\n');
  return lines.map((line, lineIdx) => (
    <React.Fragment key={lineIdx}>
      {tokenize(line).map((token, tokenIdx) => renderToken(token, tokenIdx))}
      {lineIdx < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================
const LoadingState = () => (
  <div className={styles.loadingState}>
    <div className={styles.spinner}></div>
    <p>Loading conversation details...</p>
  </div>
);

const ErrorState = ({ error, onRetry }) => (
  <div className={styles.errorState}>
    <i className={`fa fa-exclamation-triangle ${styles.errorIcon}`}></i>
    <div className={styles.errorContent}>
      <h6>Failed to load conversation</h6>
      <p>{error}</p>
    </div>
    {onRetry && (
      <button className={styles.retryBtn} onClick={onRetry}>
        <i className="fa fa-refresh"></i> Retry
      </button>
    )}
  </div>
);

const StatusBadge = ({ success, failureReason }) => {
  if (success) {
    return (
      <span className={`${styles.badge} ${styles.success}`}>
        <i className="fa fa-check-circle"></i> Success
      </span>
    );
  }
  
  return (
    <div>
      <span className={`${styles.badge} ${styles.danger}`}>
        <i className="fa fa-times-circle"></i> Failed
      </span>
      {failureReason && (
        <span className={`${styles.badge} ${styles.warning}`} style={{ marginLeft: '0.5rem' }}>
          {failureReason}
        </span>
      )}
    </div>
  );
};

const InfoRow = ({ label, value, renderValue }) => (
  <tr>
    <td className={styles.infoLabel}>{label}</td>
    <td className={styles.infoValue}>{renderValue ? renderValue() : (value || 'N/A')}</td>
  </tr>
);

const REPLY_QUOTE_PREVIEW_CHARS = 140;

const ExpandableReplyQuote = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const full = String(text);
  const needsExpand = full.length > REPLY_QUOTE_PREVIEW_CHARS;
  const shown = !needsExpand || expanded
    ? full
    : `${full.slice(0, REPLY_QUOTE_PREVIEW_CHARS).trimEnd()}…`;

  return (
    <div
      className={`${styles.replyQuote}${needsExpand ? ` ${styles.replyQuoteClickable}` : ''}`}
      onClick={needsExpand ? () => setExpanded((v) => !v) : undefined}
      role={needsExpand ? 'button' : undefined}
      tabIndex={needsExpand ? 0 : undefined}
      onKeyDown={
        needsExpand
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setExpanded((v) => !v);
              }
            }
          : undefined
      }
      title={
        needsExpand
          ? (expanded ? 'Click to collapse' : 'Click to show full quoted message')
          : 'Customer replied to this message on WhatsApp'
      }
    >
      <div className={styles.replyQuoteLabel}>Replying to</div>
      <div className={styles.replyQuoteBody}>{shown}</div>
      {needsExpand && (
        <div className={styles.replyQuoteToggle}>
          {expanded ? 'Show less' : 'Show full message'}
        </div>
      )}
    </div>
  );
};

// Enhanced Message Bubble Component
const MessageBubble = ({ message, index }) => {
  // Internal tool call/response messages are stored for memory but never shown
  if (message.role === 'tool_call' || message.role === 'tool_response') return null;

  const isNotification = message.type === 'notification' || message.type === 'notification_reply';
  const isBot = message.role === 'assistant' || message.role === 'bot';
  const isOperator = message.role === 'operator';
  const isUser = message.role === 'user';
  const isFailed = message.status === 'failed';

  // Notifications get their own centred pill-style bubble regardless of role
  if (isNotification) {
    return (
      <div key={index} style={{ display: 'flex', justifyContent: 'center', margin: '6px 0' }}>
        <div className={`${styles.messageBubble} ${styles.notification}`}>
          <div className={styles.messageRole}>
            <i className="fa fa-bell"></i>
            {message.type === 'notification_reply' ? 'Customer Reply' : 'Notification'}
          </div>
          <div className={styles.messageContent}>{parseWhatsAppMarkdown(message.content)}</div>
          {message.timestamp && (
            <div className={styles.messageTime} style={{ textAlign: 'center' }}>
              {new Date(message.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
        </div>
      </div>
    );
  }
  
  let bubbleClass = styles.bot;
  let icon = 'fa-robot';
  let label = 'Bot';
  
  if (isOperator) {
    bubbleClass = styles.operator;
    icon = 'fa-headset';
    label = 'Operator';
  } else if (isUser) {
    bubbleClass = styles.customer;
    icon = 'fa-user';
    label = 'Customer';
  }
  
  const renderMessageContent = () => {
    const mediaBase = `${config.customerApiBase}/media`;
    const stripReplyPrefix = (text) => {
      if (!text) return text;
      return String(text).replace(/^\[REPLY_TO prior message:[\s\S]*?\]\n?/i, '').trim() || text;
    };
    const bodyContent = message.reply_to
      ? stripReplyPrefix(message.content)
      : message.content;

    const quoteBlock = message.reply_to ? (
      <ExpandableReplyQuote text={message.reply_to} />
    ) : null;

    if (message.message_type === 'image' && message.media_id) {
      return (
        <>
          {quoteBlock}
          <img
            src={`${mediaBase}/${message.media_id}`}
            alt={bodyContent && bodyContent !== '[Image]' ? bodyContent : 'Image'}
            style={{ maxWidth: '100%', borderRadius: 8, display: 'block', cursor: 'pointer' }}
            onClick={() => window.open(`${mediaBase}/${message.media_id}`, '_blank')}
          />
          {bodyContent && bodyContent !== '[Image]' && (
            <div style={{ marginTop: 4 }}>{parseWhatsAppMarkdown(bodyContent)}</div>
          )}
        </>
      );
    }
    if (message.message_type === 'document' && message.media_id) {
      const fileName = message.filename || bodyContent || 'Document';
      return (
        <>
          {quoteBlock}
          <a
            href={`${mediaBase}/${message.media_id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <i className="fa fa-file-o" />
            {fileName}
          </a>
        </>
      );
    }
    if (message.message_type === 'audio' && message.media_id) {
      return (
        <>
          {quoteBlock}
          <audio
            controls
            src={`${mediaBase}/${message.media_id}`}
            style={{ width: '100%', minWidth: 220 }}
          />
        </>
      );
    }
    return (
      <>
        {quoteBlock}
        {parseWhatsAppMarkdown(bodyContent)}
      </>
    );
  };

  return (
    <div 
      key={index}
      style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}
    >
      <div className={`${styles.messageBubble} ${bubbleClass}`}>
        <div className={styles.messageRole}>
          <i className={`fa ${icon}`}></i>
          {label}
        </div>
        <div className={styles.messageContent}>{renderMessageContent()}</div>
        <div className={styles.messageFooter} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
            {message.timestamp && (
            <div className={styles.messageTime}>
                {new Date(message.timestamp).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
                })}
            </div>
            )}
            {isFailed && (
                <div className="text-danger" title={message.error || "Message failed to send"} style={{ marginLeft: '8px' }}>
                    <i className="fa fa-exclamation-circle"></i>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

const EmptyMessagesState = () => (
  <div className={styles.emptyMessages}>
    <i className={`fa fa-comments-o ${styles.emptyIcon}`}></i>
    <p>No messages found in this conversation</p>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const ConversationDetailsModal = ({ isOpen, toggle, onMinimize, isMinimized = false, customerId, conversationId: propConversationId, userId, isOngoingSession = false }) => {
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const imageInputRef = useRef(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(propConversationId);
  const [sessionPhone, setSessionPhone] = useState(null); // Store phone number for WebSocket
  const history = useHistory();
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const pendingMessagesRef = useRef([]);
  const prevIsOpenRef = useRef(false);
  const [prevConversations, setPrevConversations] = useState([]);
  const [prevLoading, setPrevLoading] = useState(false);
  const [prevError, setPrevError] = useState(null);
  const [showPrevList, setShowPrevList] = useState(false);

  // Unread counter — increments when a customer message arrives while minimized
  const { incrementUnread } = useChat();

  const mergePendingMessages = (baseConversation) => {
    if (!baseConversation) return baseConversation;

    const pendingMessages = pendingMessagesRef.current;
    if (!pendingMessages.length) return baseConversation;

    const existingMessages = baseConversation.messages || [];
    const mergedMessages = [...existingMessages];

    pendingMessages.forEach((pendingMsg) => {
      const exists = mergedMessages.some((msg) =>
        (msg.id && pendingMsg.id && msg.id === pendingMsg.id) ||
        (msg.content === pendingMsg.content && msg.timestamp === pendingMsg.timestamp)
      );
      if (!exists) mergedMessages.push(pendingMsg);
    });

    pendingMessagesRef.current = [];
    return { ...baseConversation, messages: mergedMessages };
  };

  const sortMessagesByTimestamp = (messages = []) => {
    if (!Array.isArray(messages)) return [];
    return [...messages]
      .map((msg, index) => ({ msg, index }))
      .sort((a, b) => {
        const ta = a.msg?.timestamp ? new Date(a.msg.timestamp).getTime() : null;
        const tb = b.msg?.timestamp ? new Date(b.msg.timestamp).getTime() : null;
        if (ta != null && tb != null && ta !== tb) return ta - tb;
        if (ta != null && tb == null) return -1;
        if (ta == null && tb != null) return 1;
        return a.index - b.index;
      })
      .map(({ msg }) => msg);
  };

  const normalizeConversation = (conv) => {
    if (!conv || typeof conv !== 'object') return conv;
    return {
      ...conv,
      messages: sortMessagesByTimestamp(conv.messages),
    };
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation?.messages]);

  // WebSocket connection
  useEffect(() => {
    if (!isOpen) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
        setWsConnected(false);
      }
      return;
    }

    if (isOngoingSession && (sessionPhone || userId)) {
      const wsProtocol = config.customerApiUrl.startsWith('https') ? 'wss' : 'ws';
      const wsBase = config.customerApiUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
      const sessionKey = sessionPhone || userId;
      const wsUrl = `${wsProtocol}://${wsBase}/ws/sessions/${sessionKey}`;
      console.log('WebSocket URL:', wsUrl, 'from base:', config.customerApiUrl);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsConnected(true);
        console.log('WebSocket connected for session:', sessionKey);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_message') {
            console.log('Received new message via WebSocket:', data.message);
            const content = data.message.content ? data.message.content.toString() : '';
            const role = data.message.role || '';
            if (content.startsWith('[TOOL]:') || content.includes('<ToolCall>') ||
                role === 'tool_call' || role === 'tool_response') return;
            setConversation(prev => {
              if (!prev) {
                pendingMessagesRef.current.push(data.message);
                return prev;
              }
              const messageExists = prev.messages?.some(
                msg => msg.content === data.message.content &&
                       msg.timestamp === data.message.timestamp
              );
              if (messageExists) return prev;
              return { ...prev, messages: [...(prev.messages || []), data.message] };
            });
          } else if (data.type === 'message_status') {
            setConversation(prev => {
              if (!prev || !prev.messages) return prev;
              return {
                ...prev,
                messages: prev.messages.map(msg =>
                  msg.id === data.message_id
                    ? { ...msg, status: data.status, error: data.error }
                    : msg
                ),
              };
            });
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      ws.onerror = (error) => { console.error('WebSocket error:', error); setWsConnected(false); };
      ws.onclose = () => { setWsConnected(false); console.log('WebSocket closed for session'); };

    } else if (currentConversationId || propConversationId) {
      const convId = currentConversationId || propConversationId;
      const wsProtocol = config.customerApiUrl.startsWith('https') ? 'wss' : 'ws';
      const wsBase = config.customerApiUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
      const wsUrl = `${wsProtocol}://${wsBase}/ws/conversations/${convId}`;
      console.log('WebSocket URL:', wsUrl, 'from base:', config.customerApiUrl);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsConnected(true);
        console.log('WebSocket connected for conversation');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_message') {
            console.log('Received new message via WebSocket:', data.message);
            const content = data.message.content ? data.message.content.toString() : '';
            const role = data.message.role || '';
            if (content.startsWith('[TOOL]:') || content.includes('<ToolCall>') ||
                role === 'tool_call' || role === 'tool_response') return;
            setConversation(prev => {
              if (!prev) {
                pendingMessagesRef.current.push(data.message);
                return prev;
              }
              const messageExists = prev.messages?.some(
                msg => msg.content === data.message.content &&
                       msg.timestamp === data.message.timestamp
              );
              if (messageExists) return prev;
              return { ...prev, messages: [...(prev.messages || []), data.message] };
            });
          } else if (data.type === 'message_status') {
            setConversation(prev => {
              if (!prev || !prev.messages) return prev;
              return {
                ...prev,
                messages: prev.messages.map(msg =>
                  msg.id === data.message_id
                    ? { ...msg, status: data.status, error: data.error }
                    : msg
                ),
              };
            });
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      ws.onerror = (error) => { console.error('WebSocket error:', error); setWsConnected(false); };
      ws.onclose = () => { setWsConnected(false); };
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isOpen, currentConversationId, propConversationId, userId, isOngoingSession, sessionPhone]);

  const handleMinimize = () => {
    if (onMinimize) {
      const wsProtocol = config.customerApiUrl.startsWith('https') ? 'wss' : 'ws';
      const wsBase = config.customerApiUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
      let wsUrl = null;
      if (isOngoingSession && (sessionPhone || userId)) {
        const sessionKey = sessionPhone || userId;
        wsUrl = `${wsProtocol}://${wsBase}/ws/sessions/${sessionKey}`;
      } else if (currentConversationId || propConversationId) {
        const convId = currentConversationId || propConversationId;
        wsUrl = `${wsProtocol}://${wsBase}/ws/conversations/${convId}`;
      }
      onMinimize({
        displayName: conversation?.name || null,
        displayPhone: conversation?.phone || null,
        wsUrl,
      });
    }
  };

  const fetchConversation = async () => {
    if (isOngoingSession && userId) {
      // Fetch ongoing session
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${config.customerApiBase}/sessions/${userId}/history`);
        
        if (!response.ok) {
          throw new Error(`Server responded with status ${response.status}`);
        }
        
        const data = await response.json();
        const phoneNumber = data.user_phone || data.user_id; // Use phone if available, fallback to user_id
        setSessionPhone(phoneNumber); // Store phone for WebSocket connection
        
        // Filter out internal tool messages (both legacy format and new normalised roles)
        const sessionMessages = data.messages || [];
        const filteredMessages = sessionMessages.filter(msg => {
          const content = msg.content ? msg.content.toString() : '';
          const role = msg.role || '';
          return role !== 'tool_call' && role !== 'tool_response' &&
                 !content.startsWith('[TOOL]:') && !content.includes('<ToolCall>');
        });

        setConversation(mergePendingMessages({
          user_id: data.user_id,
          customer_id: customerId,
          name: data.user_name,
          phone: data.user_phone,
          messages: filteredMessages,
          is_ongoing: true
        }));
        
        // Check if user is blocked
        if (customerId) {
          const customerResp = await fetch(`${config.customerApiBase}/customers/${customerId}/details`);
          if (customerResp.ok) {
            const customerData = await customerResp.json();
            setIsBlocked(customerData.customer?.is_blocked || false);
          }
        }
      } catch (err) {
        console.error('Error fetching session:', err);
        setError(err.message || 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    } else if (propConversationId || currentConversationId) {
      // Fetch flagged conversation
      try {
        setLoading(true);
        setError(null);
        const convId = propConversationId || currentConversationId;
        const response = await fetch(`${config.customerApiBase}/conversations/${convId}`);
        
        if (!response.ok) {
          throw new Error(`Server responded with status ${response.status}`);
        }
        
        const data = await response.json();
        setConversation(mergePendingMessages(data.conversation));
        // Store conversationId from fetched conversation
        if (data.conversation.id) {
          setCurrentConversationId(data.conversation.id);
        }
        
        // Check if user is blocked
        if (data.conversation.customer_id) {
          const customerResp = await fetch(`${config.customerApiBase}/customers/${data.conversation.customer_id}/details`);
          if (customerResp.ok) {
            const customerData = await customerResp.json();
            setIsBlocked(customerData.customer?.is_blocked || false);
          }
        }
      } catch (err) {
        console.error('Error fetching conversation:', err);
        setError(err.message || 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    } else if (customerId) {
      // Fetch customer details and latest conversation (if exists)
      try {
        setLoading(true);
        setError(null);
        
        // Fetch customer details
        const customerResponse = await fetch(`${config.customerApiBase}/customers/${customerId}/details`);
        if (!customerResponse.ok) {
          throw new Error(`Failed to fetch customer details: ${customerResponse.status}`);
        }
        const customerData = await customerResponse.json();
        const customer = customerData.customer;
        
        setIsBlocked(customer?.is_blocked || false);
        
        // Try to fetch latest conversation
        const convResponse = await fetch(`${config.customerApiBase}/customers/${customerId}/conversation`);
        if (convResponse.ok) {
          const convData = await convResponse.json();
          if (convData.conversation && convData.conversation.id) {
            setConversation(mergePendingMessages(normalizeConversation(convData.conversation)));
            setCurrentConversationId(convData.conversation.id);
          } else {
            // No conversation exists - create empty conversation object
            setConversation(mergePendingMessages(normalizeConversation({
              id: null,
              customer_id: customerId,
              user_id: customer?.user_id,
              name: customer?.name,
              phone: customer?.phone,
              messages: [],
              is_new: true
            })));
          }
        } else {
          // No conversation exists - create empty conversation object
          setConversation(mergePendingMessages(normalizeConversation({
            id: null,
            customer_id: customerId,
            user_id: customer?.user_id,
            name: customer?.name,
            phone: customer?.phone,
            messages: [],
            is_new: true
          })));
        }
      } catch (err) {
        console.error('Error fetching customer/conversation:', err);
        setError(err.message || 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    }
  };

  // When restoring from minimized state, fetch latest state once so that
  // messages received while minimized are backfilled into the chat window.
  const syncMissedMessagesOnRestore = async () => {
    try {
      if (isOngoingSession && (sessionPhone || userId)) {
        const sessionKey = sessionPhone || userId;
        const response = await fetch(`${config.customerApiBase}/sessions/${sessionKey}/history`);
        if (!response.ok) return;

        const data = await response.json();
        const sessionMessages = data.messages || [];
        const filteredMessages = sessionMessages.filter(msg => {
          const content = msg.content ? msg.content.toString() : '';
          const role = msg.role || '';
          return role !== 'tool_call' && role !== 'tool_response' &&
                 !content.startsWith('[TOOL]:') && !content.includes('<ToolCall>');
        });

        setConversation(prev => {
          const base = prev || {
            user_id: data.user_id,
            customer_id: customerId,
            name: data.user_name,
            phone: data.user_phone,
            messages: [],
            is_ongoing: true,
          };

          const merged = [...(base.messages || [])];
          filteredMessages.forEach((msg) => {
            const exists = merged.some((existing) =>
              (msg.id && existing.id && msg.id === existing.id) ||
              (existing.content === msg.content && existing.timestamp === msg.timestamp && existing.role === msg.role)
            );
            if (!exists) merged.push(msg);
          });

          return mergePendingMessages({ ...base, messages: merged });
        });
        return;
      }

      const convId = currentConversationId || propConversationId;
      if (!convId) return;

      const response = await fetch(`${config.customerApiBase}/conversations/${convId}`);
      if (!response.ok) return;

      const data = await response.json();
      if (data.conversation) {
        setConversation(prev => {
          if (!prev) return mergePendingMessages(data.conversation);

          const merged = [...(prev.messages || [])];
          (data.conversation.messages || []).forEach((msg) => {
            const exists = merged.some((existing) =>
              (msg.id && existing.id && msg.id === existing.id) ||
              (existing.content === msg.content && existing.timestamp === msg.timestamp && existing.role === msg.role)
            );
            if (!exists) merged.push(msg);
          });

          return mergePendingMessages({ ...data.conversation, messages: merged });
        });
      }
    } catch (err) {
      console.error('Error syncing missed messages on restore:', err);
    }
  };

  // Reset state when modal truly closes (NOT when minimizing)
  useEffect(() => {
    if (!isOpen && !isMinimized) {
      // Only clear state when the user actually closes the modal
      setConversation(null);
      setCurrentConversationId(null);
      setSessionPhone(null);
      setShowBookingForm(false);
      setMessageInput('');
      setSelectedImage(null);
      setIsBlocked(false);
      setError(null);
      setLoading(false);
    }
  }, [isOpen, isMinimized]);
  
  // Reset state and fetch conversation when customerId, userId, or conversationId changes
  // Also handles the initial open
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && (propConversationId || customerId || userId)) {
      // If we were minimized and are now restoring, skip re-fetch if we still have data
      if (prevOpenRef.current === false && conversation) {
        // Restoring from minimize — data still intact, just reconnect WS (handled above)
        prevOpenRef.current = true;
        return;
      }
      prevOpenRef.current = true;

      // Reset all conversation-related state when switching users
      setConversation(null);
      setLoading(true);
      setError(null);
      setIsBlocked(false);
      setMessageInput('');
      setCurrentConversationId(propConversationId || null);
      setSessionPhone(null);
      setShowBookingForm(false);
      
      // Fetch new conversation
      fetchConversation();
    }
    if (!isOpen) {
      prevOpenRef.current = false;
    }
  }, [customerId, userId, propConversationId, isOngoingSession, isOpen]);

  // Backfill messages once when restoring modal from minimized state.
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    if (isOpen && !wasOpen) {
      syncMissedMessagesOnRestore();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, isOngoingSession, sessionPhone, userId, currentConversationId, propConversationId, customerId]);

  // Poll for conversation updates when WebSocket is not connected (fallback)
  useEffect(() => {
    if (!isOpen || !conversation) return;
    
    // Keep polling flagged conversations as a consistency fallback even when WS is connected.
    if ((currentConversationId || propConversationId) && !isOngoingSession) {
      const pollInterval = setInterval(() => {
        const convId = currentConversationId || propConversationId;
        if (convId) {
          fetch(`${config.customerApiBase}/conversations/${convId}`)
            .then(res => res.json())
            .then(data => {
              if (data.conversation && data.conversation.messages) {
                setConversation(prev => {
                  if (!prev) {
                    console.log('Polling: Setting new conversation with', data.conversation.messages?.length || 0, 'messages');
                    return mergePendingMessages(data.conversation);
                  }
                  // Only update if messages changed
                  const prevMsgCount = prev.messages?.length || 0;
                  const newMsgCount = data.conversation.messages?.length || 0;
                  if (newMsgCount > prevMsgCount) {
                    console.log(`Polling: Updating conversation - ${prevMsgCount} -> ${newMsgCount} messages`);
                    return mergePendingMessages(data.conversation);
                  }
                  return prev;
                });
              }
            })
            .catch(err => console.error('Error polling conversation:', err));
        }
      }, 2000); // Poll every 2 seconds for faster updates
      
      return () => clearInterval(pollInterval);
    }
  }, [isOpen, wsConnected, currentConversationId, propConversationId, isOngoingSession, conversation]);

  const handleBlockToggle = async () => {
    if (!conversation?.customer_id) return;
    
    try {
      const response = await fetch(`${config.customerApiBase}/customers/${conversation.customer_id}/block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_blocked: !isBlocked })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update block status');
      }
      
      setIsBlocked(!isBlocked);
    } catch (err) {
      console.error('Error toggling block:', err);
      alert('Failed to update block status');
    }
  };

  const handleSendMessage = async () => {
    if ((!messageInput.trim() && !selectedImage) || sending) return;
    if (!isBlocked) {
      alert('Please block the user first to send messages');
      return;
    }

    setSending(true);
    try {
      let endpoint;
      let convId = currentConversationId || propConversationId;
      
      // If no conversation exists, create one first
      if (!convId && conversation?.customer_id && conversation?.user_id) {
        try {
          const createResponse = await fetch(`${config.customerApiBase}/conversations/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: conversation.user_id,
              customer_id: conversation.customer_id,
              intent: 'ops_takeover',
              success: false,
              failure_reason: 'Ops initiated conversation',
              source: isOngoingSession ? 'join_conversation_popup' : 'flagged_conversation_popup'
            })
          });
          
          if (createResponse.ok) {
            const createData = await createResponse.json();
            convId = createData.conversation_id;
            setCurrentConversationId(convId);
            setConversation(prev => ({ ...prev, id: convId }));
          } else {
            throw new Error('Failed to create conversation');
          }
        } catch (createErr) {
          console.error('Error creating conversation:', createErr);
          throw new Error('Failed to create conversation. Please try again.');
        }
      }
      
      if (convId) {
        endpoint = `${config.customerApiBase}/conversations/${convId}/ops-message`;
      } else if (isOngoingSession && userId) {
        endpoint = `${config.customerApiBase}/sessions/${userId}/ops-message`;
      } else {
        throw new Error('No conversation or session ID available');
      }

      let response;
      if (selectedImage) {
        const formData = new FormData();
        if (messageInput.trim()) formData.append('message', messageInput);
        formData.append('file', selectedImage.file, selectedImage.name);
        response = await fetch(endpoint, {
          method: 'POST',
          body: formData
        });
      } else {
        response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: messageInput })
        });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `Server error: ${response.status}`;
        throw new Error(errorMessage);
      }

      // Message will be added via WebSocket broadcast from backend
      // Don't add it locally to avoid duplicates
      setMessageInput('');
      setSelectedImage(null);
    } catch (err) {
      console.error('Error sending message:', err);
      alert(`Failed to send message: ${err.message || 'Unknown error'}`);
    } finally {
      setSending(false);
    }
  };

  const handleAttachImage = () => {
    imageInputRef.current?.click();
  };

  const handleImageSelected = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage({
        file,
        name: file.name,
        previewUrl: URL.createObjectURL(file)
      });
    }
    e.target.value = '';
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
  };

  const handleUnflag = async () => {
    if (!conversation?.customer_id) return;
    
    try {
      const response = await fetch(`${config.customerApiBase}/customers/${conversation.customer_id}/unflag`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error('Failed to unflag customer');
      }
      
      alert('Customer unflagged successfully');
      toggle();
    } catch (err) {
      console.error('Error unflagging:', err);
      alert('Failed to unflag customer');
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const hasMessages = Array.isArray(conversation?.messages) && conversation.messages.length > 0;

  return (
    <>
      {/* Conversation Modal - Positioned on the left */}
      <Modal 
        isOpen={isOpen} 
        toggle={toggle} 
        size="lg" 
        scrollable
        modalClassName="conversation-left-modal"
      >
        <div className={styles.modalHeader}>
          <div className={styles.headerIcon}>
            <i className="fa fa-user-circle"></i>
          </div>
          <h5 className={styles.headerTitle}>
            {isOngoingSession ? 'Active Conversation' : 'Conversation Details'}
          </h5>
          <div className={styles.headerActions}>
            {onMinimize && (
              <button
                className={styles.minimizeBtn}
                onClick={handleMinimize}
                title="Minimize"
              >
                <i className="fa fa-window-minimize"></i>
              </button>
            )}
              <button className={styles.closeBtn} onClick={toggle}>
              <i className="fa fa-times"></i>
            </button>
          </div>
        </div>
      
        <ModalBody className={styles.modalBody}>
          {loading && <LoadingState />}
          {error && <ErrorState error={error} onRetry={fetchConversation} />}
        
          {!loading && !error && conversation && (
            <>
              {/* Header Actions */}
              <div className={styles.actionBar}>
                <div className={styles.statusBadges}>
                  <span className={`${styles.badge} ${isBlocked ? styles.blocked : styles.active}`}>
                    {isBlocked ? 'User Blocked' : 'LLM Active'}
                  </span>
                  {wsConnected && (
                    <span className={`${styles.badge} ${styles.connected}`}>
                      <span className={styles.connectedDot}></span>
                      Connected
                    </span>
                  )}
                </div>
                <div className={styles.actionButtons}>
                  <button
                    className={`${styles.blockBtn} ${isBlocked ? styles.unblock : styles.block}`}
                    onClick={handleBlockToggle}
                  >
                    <i className={`fa ${isBlocked ? 'fa-unlock' : 'fa-lock'}`}></i>
                    {isBlocked ? 'Unblock User' : 'Block User'}
                  </button>
                  {isBlocked && !isOngoingSession && (
                    <button className={styles.unflagBtn} onClick={handleUnflag}>
                      <i className="fa fa-check"></i> Unflag
                    </button>
                  )}
                  <button
                    className={styles.newBookingBtn}
                    onClick={() => setShowBookingForm(!showBookingForm)}
                  >
                    <i className={`fa ${showBookingForm ? 'fa-times' : 'fa-plus'}`}></i>
                    {showBookingForm ? 'Hide Booking' : 'New Booking'}
                  </button>
                </div>
              </div>

              {/* Customer Info */}
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <i className={`fa fa-info-circle ${styles.sectionIcon}`}></i>
                  <h5 className={styles.sectionTitle}>Customer Information</h5>
                </div>
                <table className={styles.infoTable}>
                  <tbody>
                    <InfoRow 
                      label="Customer ID" 
                      renderValue={() => (
                        <code style={{ padding: '0.25rem 0.5rem', background: '#f1f3f5', borderRadius: '4px' }}>
                          {conversation.customer_id || customerId || 'N/A'}
                        </code>
                      )}
                    />
                    <InfoRow label="Name" value={conversation.name} />
                    <InfoRow label="Phone" value={conversation.phone} />
                    {!isOngoingSession && (
                      <>
                        <InfoRow 
                          label="Intent" 
                          renderValue={() => (
                            <span className={`${styles.badge} ${styles.info}`}>
                              {conversation.intent || 'N/A'}
                            </span>
                          )}
                        />
                        <InfoRow 
                          label="Status" 
                          renderValue={() => (
                            <StatusBadge 
                              success={conversation.success}
                              failureReason={conversation.failure_reason}
                            />
                          )}
                        />
                        <InfoRow 
                          label="Booking ID" 
                          renderValue={() => (
                            conversation.booking_id ? (
                              <code style={{ padding: '0.25rem 0.5rem', background: '#f1f3f5', borderRadius: '4px' }}>
                                {conversation.booking_id}
                              </code>
                            ) : 'N/A'
                          )}
                        />
                        <InfoRow label="Flagged At" value={formatDateTime(conversation.created_at)} />
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Messages */}
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <i className={`fa fa-comments ${styles.sectionIcon}`}></i>
                  <h5 className={styles.sectionTitle}>
                    {isOngoingSession ? 'Active Conversation' : 'Flagged Conversation'}
                  </h5>
                  {hasMessages && (
                    <span className={styles.sectionBadge}>
                      {Array.isArray(conversation.messages) ? conversation.messages.length : 0} messages
                    </span>
                  )}
                  {!isOngoingSession && (
                    <button
                      className={styles.prevConversationsBtn}
                      onClick={async () => {
                        const willShow = !showPrevList;
                        setShowPrevList(willShow);
                        if (willShow && prevConversations.length === 0 && customerId) {
                          try {
                            setPrevLoading(true);
                            setPrevError(null);
                            const resp = await fetch(`${config.customerApiBase}/customers/${customerId}/conversations`);
                            if (!resp.ok) {
                              const err = await resp.json().catch(() => ({}));
                              throw new Error(err.detail || 'Failed to fetch previous conversations');
                            }
                            const data = await resp.json();
                            setPrevConversations(data.conversations || []);
                          } catch (e) {
                            setPrevError(e.message);
                          } finally {
                            setPrevLoading(false);
                          }
                        }
                      }}
                    >
                      {showPrevList ? 'Hide previous' : 'See previous conversations'}
                    </button>
                  )}
                </div>

                {showPrevList && !isOngoingSession && (
                  <div className={styles.prevConversationsList}>
                    {prevLoading && (
                      <div style={{ textAlign: 'center', padding: '1rem', color: '#6c757d' }}>
                        Loading previous conversations...
                      </div>
                    )}
                    {prevError && (
                      <div style={{ padding: '1rem', background: '#fff8e1', borderRadius: '8px', color: '#f57f17' }}>
                        {prevError}
                      </div>
                    )}
                    {!prevLoading && prevConversations.length === 0 && (
                      <div style={{ color: '#6c757d', padding: '1rem' }}>No previous conversations found.</div>
                    )}
                    {!prevLoading && prevConversations.length > 0 && prevConversations.map((pc) => {
                      let snippet = '';
                      try {
                        if (pc.messages && Array.isArray(pc.messages) && pc.messages.length > 0) {
                          const first = pc.messages[0];
                          snippet = first.content ? first.content.slice(0, 120) : '';
                        }
                      } catch (e) {
                        snippet = '';
                      }
                      return (
                        <div key={pc.id} className={styles.prevConversationItem}>
                          <div className={styles.prevConversationInfo}>
                            <div className={styles.prevConversationMeta}>
                              <span className={styles.prevConversationDate}>
                                {new Date(pc.created_at).toLocaleString()}
                              </span>
                              <span className={`${styles.badge} ${pc.success ? styles.success : styles.danger}`}>
                                {pc.success ? 'Success' : 'Failed'}
                              </span>
                              <span className={`${styles.badge} ${styles.info}`}>
                                {(pc.messages && pc.messages.length) || 0} msgs
                              </span>
                            </div>
                            <div className={styles.prevConversationSnippet}>
                              {snippet || (pc.failure_reason || pc.intent || 'No summary')}
                            </div>
                          </div>
                          <button
                            className={styles.viewBtn}
                            onClick={async () => {
                              try {
                                setPrevLoading(true);
                                setPrevError(null);
                                const resp = await fetch(`${config.customerApiBase}/conversations/${pc.id}`);
                                if (!resp.ok) {
                                  const err = await resp.json().catch(() => ({}));
                                  throw new Error(err.detail || 'Failed to fetch conversation');
                                }
                                const data = await resp.json();
                                setConversation(normalizeConversation(data.conversation));
                                setShowPrevList(false);
                              } catch (e) {
                                setPrevError(e.message);
                              } finally {
                                setPrevLoading(false);
                              }
                            }}
                          >
                            View
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className={styles.messageContainer}>
                  {hasMessages ? (
                    <>
                      {conversation.messages.map((msg, index) => (
                        <MessageBubble key={index} message={msg} index={index} />
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  ) : (
                    <EmptyMessagesState />
                  )}
                </div>

                {/* Message Input */}
                <div className={styles.inputArea}>
                  {selectedImage && (
                    <div className={styles.imagePreview}>
                      <img
                        src={selectedImage.previewUrl}
                        alt=""
                        className={styles.imagePreviewImg}
                      />
                      <span className={styles.imagePreviewName}>{selectedImage.name}</span>
                      <button
                        className={styles.imagePreviewRemove}
                        onClick={handleRemoveImage}
                        title="Remove image"
                      >
                        <i className="fa fa-times"></i>
                      </button>
                    </div>
                  )}
                  <div className={styles.inputWrapper}>
                    <textarea
                      className={styles.messageInput}
                      placeholder={isBlocked ? "Type a message... (Shift+Enter for new line)" : "Block user first to send messages"}
                      value={messageInput}
                      onChange={(e) => {
                        setMessageInput(e.target.value);
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      disabled={!isBlocked || sending}
                      rows={1}
                    />
                    <div className={styles.formatHint}>
                      <span className={styles.formatLabel}>WhatsApp formatting:</span>
                      <code>*bold*</code>
                      <code>_italic_</code>
                      <code>~strike~</code>
                    </div>
                  </div>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelected}
                    style={{ display: 'none' }}
                  />
                  <button
                    className={styles.attachBtn}
                    onClick={handleAttachImage}
                    disabled={!isBlocked || sending}
                    title="Attach image"
                  >
                    <i className="fa fa-paperclip"></i>
                  </button>
                  <button
                    className={styles.sendBtn}
                    onClick={handleSendMessage}
                    disabled={!isBlocked || sending || (!messageInput.trim() && !selectedImage)}
                  >
                    {sending ? (
                      <i className="fa fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fa fa-paper-plane"></i>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        
          {!loading && !error && !conversation && (
            <div className={styles.emptyState}>
              <i className={`fa fa-inbox ${styles.emptyIcon}`}></i>
              <p>Select a customer to view details</p>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Booking Modal - Appears to the right */}
      <Modal 
        isOpen={showBookingForm} 
        toggle={() => setShowBookingForm(false)} 
        size="lg"
        modalClassName="booking-side-modal"
        backdrop={false}
        fade={false}
      >
        <div className={styles.modalHeader} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <div className={styles.headerIcon} style={{ background: 'rgba(255,255,255,0.2)' }}>
            <i className="fa fa-calendar-plus"></i>
          </div>
          <h5 className={styles.headerTitle} style={{ color: 'white' }}>New Booking</h5>
          <button className={styles.closeBtn} onClick={() => setShowBookingForm(false)} style={{ color: 'rgba(255,255,255,0.8)' }}>
            <i className="fa fa-times"></i>
          </button>
        </div>
        <ModalBody style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', padding: '15px', background: '#f8f9fa' }}>
          <NewBooking 
            key={`${showBookingForm}-${conversation?.customer_id || ''}-${conversation?.user_id || ''}-${conversation?.phone || ''}`}
            compactMode={true}
            prefillUserId={conversation?.user_id}
            prefillCustomerId={conversation?.customer_id}
            prefillUserName={conversation?.name}
            prefillUserPhone={conversation?.phone}
            onBookingCreated={() => {
              setShowBookingForm(false);
            }}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

export default ConversationDetailsModal;
